import {
  DeleteOutlined,
  DragOutlined,
  EditOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Image, Popover } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { isImageUrl } from "utils/urlUtils";

// --- Reusable Preview Components ---
// (These could also be in separate files if they grow complex)
export const ImageWithPreviewPopover = React.memo(
  ({ url }: { url: string }) => (
    <Popover
      content={
        <Image
          src={url}
          alt={"Media"}
          width={300}
          className="border border-solid border-gray-200 max-h-[500px] object-contain"
        />
      }
      trigger="hover"
    >
      <Image
        src={url}
        alt={"Thumbnail"}
        width={100}
        height={100}
        preview={false}
        className="object-cover w-full h-full group-hover:brightness-75 transition-all"
      />
    </Popover>
  )
);

export const TemplateVideoPreview = ({ url }: { url: string }) => (
  <div className="relative w-[100px] h-[100px]">
    <Image
      src={url}
      alt="Video Thumbnail"
      width={100}
      height={100}
      preview={false}
      className="object-cover w-full h-full rounded"
    />
    {/* Always show icon for template video, ensure full opacity and z-index */}
    <div className="absolute inset-0 z-5 flex items-center justify-center bg-black bg-opacity-40 pointer-events-none opacity-100">
      <PlayCircleOutlined className="text-gray-300 text-2xl" />
    </div>
  </div>
);

export const VideoWithPreviewPopover = ({
  url,
  isTemplateVideo,
}: {
  url: string;
  isTemplateVideo?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!isTemplateVideo && videoRef.current) {
      if (isOpen) {
        videoRef.current.play().catch((error) => {
          console.error("Video play failed:", error);
        });
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isOpen]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  // Popover Video Rendering (Existing Logic)
  return (
    <Popover
      content={
        <video
          ref={videoRef}
          src={url}
          width="250"
          height="auto"
          muted
          loop
          controls
          className="max-h-[500px] object-contain"
          style={{
            display: "block",
            maxWidth: "100%",
          }}
        />
      }
      trigger="hover"
      onOpenChange={handleOpenChange}
    >
      <div className="relative w-[100px] h-[100px]">
        <video
          src={url}
          muted
          width={100}
          height={100}
          className="object-cover w-full h-full rounded group-hover:brightness-75 transition-all"
          preload="metadata"
        />
        {/* Icon is visible by default, hides on hover (when popover appears) */}
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-40 opacity-100 group-hover:opacity-0 transition-opacity pointer-events-none">
          <PlayCircleOutlined className="text-gray-300 text-2xl" />
        </div>
      </div>
    </Popover>
  );
};

// --- Combined Media Item Component ---

export type MediaThumbnailProps = {
  id: string; // Needed for react-dnd even if not draggable
  index: number;
  url: string;
  isDraggable: boolean;
  isTemplateVideo: boolean;
  showEditFunctionalityForItem: boolean;
  showDeleteButton: boolean;
  handleDeleteMediaForItem: (index: number, event?: React.MouseEvent) => void;
  openDesignEditorForItem: () => void;
};

const MediaThumbnail: React.FC<MediaThumbnailProps> = React.memo(
  ({
    id,
    index,
    url,
    isDraggable,
    isTemplateVideo,
    showEditFunctionalityForItem,
    showDeleteButton,
    handleDeleteMediaForItem,
    openDesignEditorForItem,
  }) => {
    // Conditionally use the sortable hook
    const { attributes, listeners, setNodeRef, transform, isDragging, isOver } =
      useSortable({ id, disabled: !isDraggable });

    const style: React.CSSProperties = isDraggable
      ? {
          transform: CSS.Transform.toString(transform),
          zIndex: isDragging || isOver ? 10 : undefined,
          opacity: isDragging ? 0.3 : 1,
          touchAction: "none", // Important for dnd-kit touch devices
        }
      : {
          touchAction: "auto",
        }; // Default style if not draggable

    const isImage = isImageUrl(url);
    const commonContainerClasses =
      "w-[100px] h-[100px] rounded overflow-hidden relative group";
    const clickableClasses = showEditFunctionalityForItem
      ? "cursor-pointer"
      : "cursor-default";
    const containerClasses = `${commonContainerClasses} ${clickableClasses} relative group border border-solid border-gray-200`;

    // Determine li props based on draggable state
    const liProps = isDraggable
      ? { ref: setNodeRef, style, ...attributes }
      : { style };

    return (
      <li {...liProps} className="flex-shrink-0">
        {" "}
        {/* Removed touch-none here, applied conditionally in style */}
        <div
          className={containerClasses}
          onClick={
            showEditFunctionalityForItem ? openDesignEditorForItem : undefined
          }
        >
          {!isImage ? (
            <VideoWithPreviewPopover url={url} />
          ) : isTemplateVideo ? (
            <TemplateVideoPreview url={url} />
          ) : (
            <ImageWithPreviewPopover url={url} />
          )}
          {showEditFunctionalityForItem ? (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <EditOutlined className="text-white text-xl" />
            </div>
          ) : null}
          {/* Conditionally render drag handle */}
          {isDraggable && (
            <button
              type="button"
              {...listeners} // Listeners only applied if draggable
              className="absolute top-1 left-1 z-10 p-1 bg-gray-600 hover:bg-gray-800 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
              aria-label="Drag to reorder"
            >
              <DragOutlined className="text-xs block" />
            </button>
          )}
          {/* Conditionally render delete button */}
          {showDeleteButton && (
            <button
              type="button"
              onClick={(e) => handleDeleteMediaForItem(index, e)}
              className="absolute top-1 right-1 z-10 p-1 bg-red-500 hover:bg-red-700 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              aria-label="Delete media"
            >
              <DeleteOutlined className="text-xs block" />
            </button>
          )}
        </div>
      </li>
    );
  }
);

export default MediaThumbnail;
